import _ from 'lodash';

const hexStore = {};
export const getEmotionColor = (emotionName, opacity) => {
  const hex =
    {
      // OLD COLORS
      // disgusted: '#E15162', // red
      // sad: '#B54166', // blue
      // frustrated: '#F07852', // red
      // annoyed: '#F5B656',
      // disappointed: '#F7DE6E', // yellow
      // worried: '#B58CDE', // pink
      // skeptical: '#C771DA', // purple
      // neutral: '#C8C8C8', // grey
      // appreciative: '#B7F784', // light blue
      // joyful: '#1BC000', // green
      // hopeful: '#57E095',

      // NEW COLORS
      disgusted: '#980207', // deep red
      disappointed: '#FF843F', // orange
      pain: '#CB2329', // red
      frustrated: '#CB2329', // red
      annoyed: '#EF9E24', // yellow orange
      sad: '#F54F44', // red orange
      worried: '#F5B85B', // yellow orange
      skeptical: '#FFD465', // yellow gold
      confused: '#FFD465', // yellow gold
      neutral: '#C8C8C8', // grey
      appreciative: '#63B865', // light green
      joyful: '#1A944E', // darker green
      happiness: '#1A944E', // darker green
      hopeful: '#2DDF6A', //yellow green
    }[emotionName?.toLowerCase()] || '#333333';
  let { r, g, b } = hexStore[hex] || {};
  r = r || hexVals[hex[1]] * 16 + hexVals[hex[2]];
  g = g || hexVals[hex[3]] * 16 + hexVals[hex[4]];
  b = b || hexVals[hex[5]] * 16 + hexVals[hex[6]];
  hexStore[hex] = { r, g, b };
  emotionName && hex === '#333333' && console.warn(`Unknown Emotion: ${emotionName} - using default grey`);
  return `rgba(${r},${g},${b},${opacity || 1})`;
};
export const getEmotionColorHeat = (emotionName, opacity) => {
  // https://colorswall.com/palette/24579
  const hex =
    {
      disgusted: '#8b0000', // Deep red
      sad: '#971919', // Dark Red
      frustrated: '#a23333', // Red
      annoyed: '#ae4d4d', // Light Red
      disappointed: '#b96666', // Faded Red
      skeptical: '#c58080', // Faint Red
      worried: '#d19999', // Barrely Red
      neutral: '#C8C8C8', // Gray
      hopeful: '#99d18f', // Light Green
      appreciative: '#7bc86c', // Medium Green
      joyful: '#5aac44', // Green
    }[emotionName] || '#333333';
  let { r, g, b } = hexStore[hex] || {};
  r = r || hexVals[hex[1]] * 16 + hexVals[hex[2]];
  g = g || hexVals[hex[3]] * 16 + hexVals[hex[4]];
  b = b || hexVals[hex[5]] * 16 + hexVals[hex[6]];
  hexStore[hex] = { r, g, b };

  return `rgba(${r},${g},${b},${opacity || 1})`;
};

export const getEmotions = () => [
  'sad',
  'disgusted',
  'frustrated',
  'annoyed',
  'disappointed',
  'worried',
  'skeptical',
  'neutral',
  'appreciative',
  'hopeful',
  'joyful',
];

export const getEmotionalDuration = (call, selectedParticipant) => {
  return selectedParticipant ? parseFloat(selectedParticipant?.speaking_duration_seconds) : call?.duration_seconds;
};
export const getEmotionalBreakdownFromMessages = (call, { participant, emotion, variable, timeWindowEnd } = {}) => {
  /*
      This function is slower than the standard emotional breakdown and should only be used
      if we need detailed filtering on the message level.

      Returns an ordered list of emotions with the ratio of time spent speaking in that
      emotional state vs time spent speaking (customer only).

      Optional Filters:

      participant - filter it only to a specific participant
      emotion - filter it only to a specific participant
      beforeTime - filter it only to before a specific time

      Example Response:
      [
        { emotion: 'sad', ratio: 0.53 },
        { emotion: 'neutral', ratio: 0.28 },
        ...
      ]
    */
  const { messages } = call;
  const selectedParticipant = participant;
  const selectedVariable = variable;

  let emotionBreakdown = {};
  let unfilteredEmotionBreakdown = {};

  messages.forEach((msg) => {
    if (msg.emotion === 'neutral') return;
    let timeSpent = msg.duration;
    if (msg.start_time_seconds < timeWindowEnd && msg.end_time_seconds > timeWindowEnd) {
      timeSpent = timeWindowEnd - msg.start_time_seconds;
    }

    unfilteredEmotionBreakdown[msg.emotion] = unfilteredEmotionBreakdown[msg.emotion] + timeSpent || timeSpent;
    const isParticipantMatch = !selectedParticipant || selectedParticipant?.id === msg?.speaker?.id;
    const isVariableMatch = !selectedVariable || msg[variable];
    const isTimeMatch = !timeWindowEnd || msg.start_time_seconds < timeWindowEnd;
    if (isParticipantMatch && isTimeMatch && isVariableMatch) {
      emotionBreakdown[msg.emotion] = emotionBreakdown[msg.emotion] + timeSpent || timeSpent;
    }
  });

  let totalEmotionToDurationMap = _.reduce(unfilteredEmotionBreakdown, (r, v, k) => ({ ...r, [k]: 0 }), {});
  _.forEach(emotionBreakdown, (duration, emotion) => {
    totalEmotionToDurationMap[emotion] = (totalEmotionToDurationMap[emotion] || 0) + duration;
  });

  let totalEmotionToDurationMapUnfiltered = _.reduce(unfilteredEmotionBreakdown, (r, v, k) => ({ ...r, [k]: 0 }), {});
  _.forEach(unfilteredEmotionBreakdown, (duration, emotion) => {
    totalEmotionToDurationMapUnfiltered[emotion] = (totalEmotionToDurationMapUnfiltered[emotion] || 0) + duration;
  });

  const totalDurationUnfiltered = _.sum(_.values(totalEmotionToDurationMapUnfiltered));
  const totalDuration = _.sum(_.values(totalEmotionToDurationMap)) || 1; // avoid infinite denominators at start of call

  const emotions = _.reduce(
    totalEmotionToDurationMap,
    (r, duration, emotion) => [
      ...r,
      {
        duration,
        emotion: emotion,
        ratio: duration / totalDuration,
        percentage: (duration * 100) / totalDuration,
        ratioUnfiltered: (totalEmotionToDurationMapUnfiltered[emotion] || 0) / totalDurationUnfiltered,
        percentageUnfiltered: (duration * 100) / totalDurationUnfiltered,
      },
    ],
    []
  );
  return _.orderBy(emotions, selectedParticipant || selectedVariable ? 'ratioUnfiltered' : 'ratio', 'desc');
};

export const getEmotionalBreakdown = (call, { participant, emotion, beforeTime } = {}) => {
  /*
      Returns an ordered list of emotions with the ratio of time spent speaking in that
      emotional state vs time spent speaking (customer only).

      Optional Filters:

      participant - filter it only to a specific participant
      emotion - filter it only to a specific participant

      Example Response:
      [
        { emotion: 'sad', ratio: 0.53 },
        { emotion: 'neutral', ratio: 0.28 },
        ...
      ]
    */
  const { summary } = call;
  const selectedParticipant = participant;
  // const selectedEmotion = emotion;

  let totalEmotionToMessageCountMap = _.reduce(summary?.emotions_breakdown, (r, v, k) => ({ ...r, [k]: 0 }), {});
  _.forEach(selectedParticipant ? selectedParticipant.participant_emotion_breakdown || {} : summary?.emotions_breakdown, (number, emotion) => {
    totalEmotionToMessageCountMap[emotion] = (totalEmotionToMessageCountMap[emotion] || 0) + number;
  });

  let totalEmotionToMessageCountMapUnfiltered = _.reduce(summary?.emotions_breakdown, (r, v, k) => ({ ...r, [k]: 0 }), {});
  _.forEach(summary?.emotions_breakdown, (number, emotion) => {
    totalEmotionToMessageCountMapUnfiltered[emotion] = (totalEmotionToMessageCountMapUnfiltered[emotion] || 0) + number;
  });

  // Hide neutral emotions
  const totalMessageCountFull = _.sum(_.values(totalEmotionToMessageCountMap));
  delete totalEmotionToMessageCountMap.neutral;
  const totalMessageCount = _.sum(_.values(totalEmotionToMessageCountMap));
  const totalMessageCountUnfiltered = _.sum(_.values(totalEmotionToMessageCountMapUnfiltered));
  const emotions = _.reduce(
    totalEmotionToMessageCountMap,
    (r, count, emotion) => [
      ...r,
      {
        emotion: emotion,
        ratio: count / totalMessageCount,
        ratioUnfiltered: (totalEmotionToMessageCountMapUnfiltered[emotion] || 0) / totalMessageCountUnfiltered,
        percentage: (count * 100) / totalMessageCount,
        ratioFull: count / totalMessageCountFull,
        percentageFull: (count * 100) / totalMessageCountFull,
      },
    ],
    []
  );
  return _.orderBy(emotions, selectedParticipant ? 'ratioUnfiltered' : 'ratio', 'desc');
};
export const getParticipantBreakdown = (call, { participant, emotion, variable, timeWindowEnd } = {}) => {
  const { participants, messages } = call;
  const selectedEmotion = emotion;
  const selectedVariable = variable;

  let talkTimeMap = {};
  let total_duration = 0;
  _.forEach(messages, (msg) => {
    const { speaker, emotion, duration, end_time_seconds, start_time_seconds } = msg;
    const isEmotionMatch = !selectedEmotion || emotion === speaker?.id;
    const isVariableMatch = !selectedVariable || msg[variable];
    const isTimeMatch = !timeWindowEnd || start_time_seconds < timeWindowEnd;
    if (!isEmotionMatch || !isVariableMatch || !isTimeMatch) return;
    let timeSpent = duration;
    if (start_time_seconds < timeWindowEnd && end_time_seconds > timeWindowEnd) {
      timeSpent = timeWindowEnd - start_time_seconds;
    }
    talkTimeMap[speaker?.id] = (talkTimeMap[speaker?.id] || 0) + timeSpent;
    total_duration += timeSpent;
  });
  total_duration = total_duration || 1; // avoid infinite denominators at start of call
  const breakdown = participants.map((participant) => {
    const time = talkTimeMap[participant?.id] || 0;
    return {
      participant,
      duration: time,
      ratio: time / total_duration,
      percentage: parseInt((time * 100) / total_duration),
    };
  });

  return _.orderBy(breakdown, 'ratio', 'desc');
};

const hexVals = {
  0: 0,
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6,
  7: 7,
  8: 8,
  9: 9,
  A: 10,
  B: 11,
  C: 12,
  D: 13,
  E: 14,
  F: 15,
  a: 10,
  b: 11,
  c: 12,
  d: 13,
  e: 14,
  f: 15,
};
